/**
 * Create or edit one scoring rule: its name, points, conditions and, for a
 * lead rule, the streams it applies to.
 *
 * The conditions are built with the routing filter editor itself, so a rule
 * says exactly what a group or destination filter would, and the engine runs
 * it through the same evaluator. A lead rule offers the filters that read
 * the lead (not history or delivery, which have not happened when a lead is
 * scored); a person rule offers field conditions over what a person has.
 *
 * As a lead rule is edited, the preview beside it scores the newest leads
 * with this draft standing in for the saved rule, so the effect is seen
 * before anything is saved.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Drawer, Grid, Group, MultiSelect, NumberInput, Stack, Switch, Text, TextInput,
} from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { useCampaigns } from '../../api/queries';
import { useProperties } from '../../api/properties';
import {
  useScorePreview, useScoringEdits, useScoringSettings, type ScoreObject, type ScorePreviewBody, type ScoringRule,
} from '../../api/scoring';
import { useVocab } from '../../vocab';
import {
  asFilters, countIncomplete, fieldItem, SmartFilters, syntheticNumberField, toStored, type FieldSource,
  type SmartFilter,
} from '../SmartFilterEditor';
import { ScorePreview } from './ScorePreview';
import { signed } from './scoringShared';

/** What a person rule can test, beside the person's own properties. */
export interface PersonFact {
  key: string;
  label: string;
}

/** Facts that are counts are numbers; the rest are text or yes/no. */
const NUMBER_FACTS = new Set(['submissions', 'emails_opened', 'emails_clicked', 'open_deals', 'won_deals', 'lost_deals']);

export function ScoringRuleDrawer({
  opened,
  object,
  rule,
  allowTypes,
  facts,
  onClose,
}: {
  opened: boolean;
  object: ScoreObject;
  /** The rule being edited; null to make one. */
  rule: ScoringRule | null;
  /** Filter types this kind of rule may use, as the server says. */
  allowTypes: string[];
  facts: PersonFact[];
  onClose: () => void;
}) {
  const v = useVocab();
  const { create, update } = useScoringEdits();
  const settings = useScoringSettings(opened);
  const campaigns = useCampaigns(opened && object === 'submission');
  const personDefs = useProperties('person', { enabled: opened && object === 'person' }).data?.properties;
  const personProps = useMemo(() => personDefs ?? [], [personDefs]);
  const narrow = useMediaQuery('(max-width: 62em)') ?? false;

  const [name, setName] = useState('');
  const [points, setPoints] = useState<number | string>(10);
  const [streams, setStreams] = useState<string[]>([]);
  const [active, setActive] = useState(true);
  const [andFilters, setAndFilters] = useState<SmartFilter[]>([]);
  const [orFilters, setOrFilters] = useState<SmartFilter[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!opened) return;
    setName(rule?.name ?? '');
    setPoints(rule?.points ?? 10);
    setStreams(rule?.campaign_ids ?? []);
    setActive(rule?.active ?? true);
    setAndFilters(asFilters(rule?.conditions, 'and'));
    setOrFilters(asFilters(rule?.conditions, 'or'));
    setError('');
  }, [opened, rule]);

  // A person rule picks from the person's facts and fields.
  const fields: FieldSource | undefined = useMemo(() => {
    if (object !== 'person') return undefined;
    const factDefs = facts.map((f) =>
      NUMBER_FACTS.has(f.key) ? syntheticNumberField(f.key, f.label) : { ...syntheticNumberField(f.key, f.label), type: 'text' as const },
    );
    return {
      groups: [
        { group: `About the ${v.one('person')}`, items: facts.map((f) => fieldItem(f.key, f.label)) },
        { group: v.Many('property'), items: personProps.map((d) => fieldItem(d.key, d.label)) },
      ],
      defs: [...factDefs, ...personProps],
    };
  }, [object, facts, personProps, v]);

  const conditions = toStored(andFilters, orFilters);
  const enabledCount = conditions.filter((c) => c.enabled).length;
  const incomplete = countIncomplete([...andFilters, ...orFilters]);
  const pts = typeof points === 'number' ? points : Number(points);
  const ready = enabledCount > 0 && incomplete === 0 && Number.isFinite(pts) && pts !== 0;

  // The draft, standing in for this rule among the saved ones.
  const draftBody: ScorePreviewBody | null = ready && object === 'submission'
    ? {
      draft: {
        id: rule?.id ?? null,
        name: name.trim() || 'This rule',
        conditions,
        points: pts,
        campaign_ids: streams,
        active: true,
      },
    }
    : null;
  const [debounced] = useDebouncedValue(draftBody, 400);
  const preview = useScorePreview(debounced, opened && object === 'submission');

  async function save() {
    setError('');
    if (!name.trim()) return setError('Give the rule a name.');
    if (!Number.isFinite(pts) || pts === 0) return setError('Points are a whole number, and not 0.');
    if (enabledCount === 0) return setError('Add at least one condition that is switched on.');
    if (incomplete > 0) return setError('Every field condition needs a field.');
    try {
      if (rule) {
        await update.mutateAsync({
          id: rule.id,
          body: {
            name: name.trim(),
            points: pts,
            conditions,
            active,
            ...(object === 'submission' ? { campaign_ids: streams } : {}),
          },
        });
      } else {
        await create.mutateAsync({
          object,
          name: name.trim(),
          points: pts,
          conditions,
          active,
          ...(object === 'submission' ? { campaign_ids: streams } : {}),
        });
      }
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const what = object === 'person' ? v.one('person') : v.one('lead');
  const editor = (
    <Stack gap="md">
      <Grid gutter="sm">
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <TextInput
            label="Name"
            description="Shown in every score's breakdown."
            placeholder="Homeowner"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            data-autofocus
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <NumberInput
            label="Points"
            description="Negative takes points away."
            value={points}
            onChange={setPoints}
            allowDecimal={false}
            min={-1000}
            max={1000}
            data-testid="rule-points"
          />
        </Grid.Col>
      </Grid>
      {object === 'submission' && (
        <MultiSelect
          label={`Only for these ${v.many('campaign')}`}
          description={`Left empty, the rule scores every ${v.one('lead')}.`}
          placeholder={`Every ${v.one('campaign')}`}
          searchable
          clearable
          data={(campaigns.data?.campaigns ?? [])
            .filter((c) => !c.is_reference_list)
            .map((c) => ({ value: c.id, label: c.name }))}
          value={streams}
          onChange={setStreams}
        />
      )}
      <Switch
        label="On"
        description={`Off, the rule adds nothing to any ${what}.`}
        checked={active}
        onChange={(e) => setActive(e.currentTarget.checked)}
      />
      <Card withBorder padding="sm">
        <Stack gap="sm">
          <Text size="sm" fw={600}>{`When the ${what} matches`}</Text>
          <SmartFilters
            andFilters={andFilters}
            orFilters={orFilters}
            enabled
            setAndFilters={setAndFilters}
            setOrFilters={setOrFilters}
            allowTypes={allowTypes}
            fields={fields}
            noun={what}
            campaignIds={object === 'submission' ? streams : undefined}
          />
        </Stack>
      </Card>
      <Text size="sm" c="dimmed">
        {`A condition that cannot be checked (a list that cannot be read, for instance) adds nothing; the ${what} is never turned away because of scoring.`}
      </Text>
      {error && <Alert color="red" variant="light">{error}</Alert>}
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={create.isPending || update.isPending}>
          {rule ? 'Save rule' : 'Add rule'}
        </Button>
      </Group>
    </Stack>
  );

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={object === 'submission' ? '72rem' : 'lg'}
      title={rule ? `Edit ${rule.name}` : `New ${what} rule`}
    >
      {object === 'submission' ? (
        <Grid gutter="lg">
          <Grid.Col span={narrow ? 12 : 7}>{editor}</Grid.Col>
          <Grid.Col span={narrow ? 12 : 5}>
            <Card withBorder padding="sm" data-testid="draft-preview">
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" fw={600}>Preview</Text>
                  {ready && <Text size="xs" c="dimmed">{`With this rule at ${signed(pts)}`}</Text>}
                </Group>
                {ready ? (
                  <ScorePreview
                    data={preview.data}
                    loading={preview.isFetching}
                    error={preview.error}
                    settings={settings.data}
                    highlightRuleId={rule?.id ?? undefined}
                  />
                ) : (
                  <Text size="sm" c="dimmed">
                    Add a condition and points to see how the newest {v.many('lead')} would score.
                  </Text>
                )}
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>
      ) : (
        editor
      )}
    </Drawer>
  );
}
